import React, { FC, useCallback, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { GestureResponderEvent } from 'react-native';

import CtaButton from 'components/top-service-mode/actions/cta-button';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { appendObjectToQueryString } from 'navigation/utils';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { ServiceMode, useOrderContext } from 'state/order';
import { useSmartAlert } from 'state/smart-alert';
import { useStoreContext } from 'state/store';
import { theme } from 'styles/configure-theme';
import { routes } from 'utils/routing';

import ChooseLocation from './actions/choose-location';
import {
  ArrowRightIcon,
  ButtonWrapper,
  ClickableWrapper,
  Container,
  Details,
  DetailsContainer,
  Heading,
  IconContainer,
  IconStyled,
  SmartAlertText,
} from './styled';
import { ITopServiceModeProps } from './types';

// Maximum allowed number of characters for the selected location address as per DS
const maxChars = 24;

/**
 * Abstraction point for top service UI/feature roll out of future ordering
 */
export const TopServiceModeView: FC<React.PropsWithChildren<ITopServiceModeProps>> = ({
  heading,
  details,
  icon,
  rbiOrderId,
  openModal,
  isInCart = false,
}) => {
  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuthContext();
  const { noStoreSelected, store: restaurant } = useStoreContext();
  const { serviceMode, curbsidePickupOrderId, isPreConfirmCurbside } = useOrderContext();
  const router = useRouter();
  const isDesktop = useIsDesktopBreakpoint();
  const { distanceText, isLoading, canShowDistance } = useDistanceText({
    restaurant,
    shouldUseUserCoordinatesIfAvailable: true,
  });
  const { tsmSmartAlert } = useSmartAlert();

  const customDetails = useMemo(
    () =>
      typeof details !== 'string' || details.length <= maxChars || isDesktop
        ? details
        : `${details.substring(0, maxChars)}...`,
    [details, isDesktop]
  );

  const handleServiceModeChange = useCallback(() => {
    logEvent(CustomEventNames.TOP_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
    });

    if (isPreConfirmCurbside() && isAuthenticated) {
      return openModal && openModal();
    }

    if (serviceMode === ServiceMode.DELIVERY) {
      // this preserves the functionality of dismiss delivery address and you see the store locator screen
      router.navigate(routes.storeLocator);
      router.navigate(routes.deliveryAddress);
      return;
    }

    router.navigate(routes.storeLocator);
  }, [isPreConfirmCurbside, isAuthenticated, serviceMode, router, openModal]);

  const handleNavigateToOrderConfirmation = (rbiOrderId: string, showToast?: boolean) => {
    logEvent(CustomEventNames.TOP_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
      'Order Id': rbiOrderId,
    });
    router.navigate(
      appendObjectToQueryString(`${routes.orderConfirmation}/${rbiOrderId}`, { showToast })
    );
  };

  const getCtaButton = () => {
    if (isPreConfirmCurbside()) {
      return (
        <CtaButton
          text={formatMessage({ id: 'confirmArrival' })}
          cta={() => handleNavigateToOrderConfirmation(curbsidePickupOrderId, true)}
        />
      );
    } else if (rbiOrderId && !isInCart) {
      return (
        <CtaButton
          text={formatMessage({ id: 'trackOrder' })}
          cta={() => handleNavigateToOrderConfirmation(rbiOrderId)}
        />
      );
    } else if (serviceMode === ServiceMode.DELIVERY) {
      return (
        <CtaButton
          text={formatMessage({ id: 'change' })}
          isLoading={isLoading}
          cta={handleServiceModeChange}
          icon={<ArrowRightIcon />}
        />
      );
    }

    if (canShowDistance) {
      return (
        <CtaButton
          text={distanceText}
          isLoading={isLoading}
          cta={handleServiceModeChange}
          icon={<ArrowRightIcon />}
          showDistanceText
          bold
          accessibilityLabel={formatMessage({ id: 'changeStores' })}
        />
      );
    }

    return <CtaButton cta={handleServiceModeChange} icon={<ArrowRightIcon />} />;
  };

  const handleSmartAlertPress = (e: GestureResponderEvent) => {
    e.stopPropagation();
    if (!tsmSmartAlert?.linkUrl) {
      return;
    }
    router.navigate(tsmSmartAlert?.linkUrl);
  };

  const button = getCtaButton();

  return (
    <Container testID="top-service-mode">
      <ClickableWrapper
        dd-action-name="top-service-mode-wrapper"
        testID="top-service-mode-wrapper"
        onPress={handleServiceModeChange}
        accessibilityRole="link"
        isInCart={isInCart}
        hasAlert={!!tsmSmartAlert}
      >
        {!isInCart && (
          <IconContainer>
            <IconStyled color={theme.token('icon-default')} variant={icon} />
          </IconContainer>
        )}
        <DetailsContainer justifyContent={noStoreSelected ? 'space-between' : null}>
          {!isInCart && <Heading>{heading}</Heading>}
          {details ? (
            <Details
              accessibilityLabel={`${formatMessage({ id: 'address' })}: ${details}`}
              isInCart={isInCart}
            >
              {isInCart ? details : customDetails}
            </Details>
          ) : (
            <ChooseLocation testID="top-service-choose-location" />
          )}
          {!!tsmSmartAlert?.message && (
            <SmartAlertText onPress={handleSmartAlertPress}>{tsmSmartAlert.message}</SmartAlertText>
          )}
        </DetailsContainer>
      </ClickableWrapper>
      {button && !noStoreSelected && <ButtonWrapper>{button}</ButtonWrapper>}
    </Container>
  );
};
